import { ref } from 'vue'
import type { Ref } from 'vue'
import type { ScheduleItem, ScheduleItemFormData } from '@/types/scheduleItems'
import {
  listScheduleItems as apiListScheduleItems,
  createScheduleItem as apiCreateScheduleItem,
  updateScheduleItem as apiUpdateScheduleItem,
  deleteScheduleItem as apiDeleteScheduleItem,
} from '@/api/scheduleItems'

const items = ref<ScheduleItem[]>([])
const loading = ref(false)
const saving = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref<string | null>(null)

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { data?: unknown } }).response !== null &&
    typeof (e as { response: { data: unknown } }).response.data === 'object'
  )
}

async function fetchScheduleItems(_eventSlug: string, editionId: number): Promise<void> {
  loading.value = true
  generalError.value = null
  try {
    items.value = await apiListScheduleItems(editionId)
  } catch {
    generalError.value = 'editions.schedule.errors.load_failed'
  } finally {
    loading.value = false
  }
}

async function createScheduleItem(
  _eventSlug: string,
  editionId: number,
  data: ScheduleItemFormData,
): Promise<ScheduleItem | null> {
  saving.value = true
  errors.value = {}
  generalError.value = null
  try {
    const created = await apiCreateScheduleItem(editionId, data)
    items.value = [...items.value, created]
    return created
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'editions.schedule.errors.save_failed'
    }
    return null
  } finally {
    saving.value = false
  }
}

async function updateScheduleItem(
  _eventSlug: string,
  editionId: number,
  itemId: number,
  data: ScheduleItemFormData,
): Promise<ScheduleItem | null> {
  saving.value = true
  errors.value = {}
  generalError.value = null
  try {
    const updated = await apiUpdateScheduleItem(editionId, itemId, data)
    items.value = items.value.map((it) => (it.id === itemId ? updated : it))
    return updated
  } catch (e: unknown) {
    if (isValidationError(e)) {
      errors.value = e.response.data.errors
    } else {
      generalError.value = 'editions.schedule.errors.save_failed'
    }
    return null
  } finally {
    saving.value = false
  }
}

async function deleteScheduleItem(
  _eventSlug: string,
  editionId: number,
  itemId: number,
): Promise<boolean> {
  saving.value = true
  generalError.value = null
  try {
    await apiDeleteScheduleItem(editionId, itemId)
    items.value = items.value.filter((it) => it.id !== itemId)
    return true
  } catch {
    generalError.value = 'editions.schedule.errors.delete_failed'
    return false
  } finally {
    saving.value = false
  }
}

export function useScheduleItems(): {
  items: Ref<ScheduleItem[]>
  loading: Ref<boolean>
  saving: Ref<boolean>
  errors: Ref<Record<string, string[]>>
  generalError: Ref<string | null>
  fetchScheduleItems: (eventSlug: string, editionId: number) => Promise<void>
  createScheduleItem: (
    eventSlug: string,
    editionId: number,
    data: ScheduleItemFormData,
  ) => Promise<ScheduleItem | null>
  updateScheduleItem: (
    eventSlug: string,
    editionId: number,
    itemId: number,
    data: ScheduleItemFormData,
  ) => Promise<ScheduleItem | null>
  deleteScheduleItem: (
    eventSlug: string,
    editionId: number,
    itemId: number,
  ) => Promise<boolean>
} {
  return {
    items,
    loading,
    saving,
    errors,
    generalError,
    fetchScheduleItems,
    createScheduleItem,
    updateScheduleItem,
    deleteScheduleItem,
  }
}
